import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, User, MeResponse } from '../api';

interface AuthUser extends User {
  influencer?: {
    id: number;
    stage_name: string;
    first_name: string;
    last_name: string;
  };
}

interface LoginResult {
  success: boolean;
  requiresVerification?: boolean;
  message?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string, turnstileToken: string) => Promise<LoginResult>;
  verifyAdminCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const userData: MeResponse = await api.getMe();
      setUser({
        id: userData.id,
        email: userData.email,
        role: userData.role,
        storefront_id: userData.storefront_id,
        influencer: userData.influencer,
      });
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  /**
   * Login with email, password, and Turnstile token
   * For admin users, this returns requiresVerification = true
   * The caller should then redirect to the 2FA verification page
   */
  const login = async (
    email: string,
    password: string,
    turnstileToken: string
  ): Promise<LoginResult> => {
    const result = await api.login(email, password, turnstileToken);

    if (result.requiresVerification) {
      // Admin needs 2FA
      return {
        success: false,
        requiresVerification: true,
        message: result.message,
      };
    }

    // Influencer or admin (if 2FA disabled) - login complete
    if (result.user) {
      await refresh();
      return { success: true };
    }

    throw new Error('Unexpected login response');
  };

  /**
   * Verify admin 2FA code and complete login
   */
  const verifyAdminCode = async (email: string, code: string) => {
    await api.verifyAdminCode(email, code);
    await refresh();
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyAdminCode, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
